import React, { useState } from 'react';
import { Lock, AlertCircle } from 'lucide-react';

interface TopupPasswordModalProps {
  isOpen: boolean;
  onPasswordSubmit: (password: string) => boolean;
}

/**
 * Full-screen password modal for topup page access
 * Cannot be dismissed - user must enter correct password
 * Provides user feedback on incorrect password attempts
 */
export default function TopupPasswordModal({
  isOpen,
  onPasswordSubmit
}: TopupPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Simulate validation delay for better UX
    setTimeout(() => {
      const isValid = onPasswordSubmit(password);

      if (!isValid) {
        setError('Mật khẩu không đúng. Vui lòng thử lại.');
        setPassword('');
      }

      setIsSubmitting(false);
    }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-md w-full p-8 shadow-2xl border border-gray-200">
        {/* Lock Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
            <Lock className="w-8 h-8 text-indigo-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Xác thực truy cập
        </h2>
        <p className="text-gray-600 text-center mb-6">
          Vui lòng nhập mật khẩu để truy cập trang nạp tiền
        </p>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="topup-password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Mật khẩu
            </label>
            <input
              id="topup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Nhập mật khẩu"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-lg"
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!password || isSubmitting}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isSubmitting ? 'Đang xác thực...' : 'Xác nhận'}
          </button>
        </form>

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Liên hệ quản trị viên nếu bạn quên mật khẩu
        </p>
      </div>
    </div>
  );
}
